import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPublicShareLink, downloadFromShareLink } from "@api/share-links.api";
import { extractErrorMessage } from "@api/axios";
import type { PublicShareLinkInfo } from "@/types";
import { AppMark } from "@components/AppMark";
import { SHARE_LINK_PASSWORD_MIN_LENGTH } from "@/constants/app";
import { formatFileSize } from "@/utils/format";

export default function PublicDownload() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<PublicShareLinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    getPublicShareLink(token)
      .then(setInfo)
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = async () => {
    if (!token) return;
    if (info?.hasPassword && password.length < SHARE_LINK_PASSWORD_MIN_LENGTH) {
      setError(t("shareLink.passwordTooShort", { min: SHARE_LINK_PASSWORD_MIN_LENGTH }));
      return;
    }
    setError(null);
    setDownloading(true);
    try {
      const blob = await downloadFromShareLink(token, password || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      if (info?.files.length === 1) {
        a.download = info.files[0].originalName;
      } else {
        a.download = "download.zip";
      }

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloaded(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F3F4F6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 480, width: "100%", borderRadius: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <AppMark
                size={48}
                fontSize={18}
                borderRadius={2}
                sx={{ bgcolor: "#1A1A1A", color: "common.white" }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
              }}
            >
              {t("publicDownload.title")}
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && !downloaded && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {downloaded && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: "#10B981", mb: 2 }} />
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("publicDownload.successTitle")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("publicDownload.successBody")}
              </Typography>
            </Box>
          )}

          {info && !downloaded && (
            <>
              <List dense>
                {info.files.map((file, i) => (
                  <ListItem key={i}>
                    <ListItemText
                      primary={file.originalName}
                      secondary={formatFileSize(file.size)}
                    />
                  </ListItem>
                ))}
              </List>

              {info.hasPassword && (
                <TextField
                  label={t("publicDownload.passwordLabel")}
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  size="small"
                  fullWidth
                  error={Boolean(
                    password.length > 0 && password.length < SHARE_LINK_PASSWORD_MIN_LENGTH,
                  )}
                  helperText={t("shareLink.passwordHint", { min: SHARE_LINK_PASSWORD_MIN_LENGTH })}
                  sx={{ mt: 2, mb: 2 }}
                  slotProps={{
                    input: {
                      startAdornment: <LockIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    },
                  }}
                />
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={
                  downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />
                }
                onClick={handleDownload}
                disabled={
                  downloading ||
                  (info.hasPassword && password.length < SHARE_LINK_PASSWORD_MIN_LENGTH)
                }
                sx={{
                  mt: 2,
                  py: 1.5,
                  bgcolor: "#0066FF",
                  "&:hover": { bgcolor: "#0052CC" },
                }}
              >
                {downloading ? t("publicDownload.downloading") : t("publicDownload.download")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
